'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import type { PostLoginAction } from './postLoginActions';
import { PostLoginActionType, postLoginActionsStorage } from './postLoginActions';
import { ChangeEmailModalDynamic } from './ChangeEmailModal.dynamic';
import { ChangeBillingAddressModalDynamic } from './ChangeBillingAddressModal.dynamic';
import { TACReconfirmationModalDynamic } from './TACReconfirmationModal.dynamic';

/**
 * PostLoginActionsHandler monitors localStorage for pending post-login actions
 * and displays them sequentially (one at a time) to the user.
 *
 * Actions are removed from the queue when:
 * - User successfully completes the action
 * - User dismisses/closes the modal
 * - User logs out
 */
export const PostLoginActionsHandler = () => {
  const serializedActions = useSyncExternalStore(
    postLoginActionsStorage.subscribe,
    postLoginActionsStorage.getSerializedActions,
    () => null,
  );
  const currentAction: PostLoginAction | undefined =
    postLoginActionsStorage.parseActions(serializedActions)[0];
  const hasUnknownAction =
    currentAction !== undefined && !Object.values(PostLoginActionType).includes(currentAction.type);
  const { loggedIn } = useLoginState();

  // Clear actions when user logs out
  useEffect(() => {
    if (!loggedIn && postLoginActionsStorage.getActions().length > 0) {
      postLoginActionsStorage.clearActions();
    }
  }, [loggedIn]);

  // Unknown action type, remove it. Only from the queue this render saw: StrictMode runs the
  // effect twice, and a second removal would drop the valid action behind it.
  useEffect(() => {
    if (hasUnknownAction && postLoginActionsStorage.getSerializedActions() === serializedActions) {
      postLoginActionsStorage.removeFirstAction();
    }
  }, [hasUnknownAction, serializedActions]);

  // Handler for completing/dismissing an action
  const handleComplete = () => {
    postLoginActionsStorage.removeFirstAction();
  };

  if (!currentAction) return null;

  // Render appropriate modal based on action type
  switch (currentAction.type) {
    case PostLoginActionType.CHECK_EMAIL:
      return <ChangeEmailModalDynamic action={currentAction} onClose={handleComplete} />;
    case PostLoginActionType.CHECK_BILLING_ADDRESS:
      return <ChangeBillingAddressModalDynamic action={currentAction} onClose={handleComplete} />;
    case PostLoginActionType.TAC_RECONFIRMATION:
      return <TACReconfirmationModalDynamic action={currentAction} onClose={handleComplete} />;
    default:
      return null;
  }
};
